/**
 * Mojibake repair for GQ assistant data (GQ Support + GQ Search).
 *
 * Some stored/returned text was saved "mojibaked": a UTF-8 string whose bytes
 * were decoded as Latin-1/Windows-1252 — sometimes several layers deep, because
 * already-mangled text gets fed back through the AIS pipeline and mangled again.
 * e.g. a curly quote “ (U+201C) can surface as "Ã¢â‚¬Å“" or worse.
 *
 * fetch's res.json() already decodes the wire bytes as UTF-8 correctly, so the
 * corruption is baked into the data itself; we undo it here on the way in.
 *
 * The approach is the same one `ftfy` uses: reverse the corruption exactly the
 * way it happened. Turn each character back into the byte it came from and decode
 * those bytes as UTF-8, repeating until the string stops changing. A { fatal:true }
 * decoder throws on anything that isn't valid UTF-8, so genuinely-clean text (and
 * already-repaired text) is returned untouched instead of being mangled. This
 * fixes ANY mis-encoded character at ANY depth — no hardcoded character list.
 */

// Windows-1252 high range (0x80–0x9F) maps these bytes to specific Unicode code
// points — the reverse map lets us turn a mis-decoded char back into its byte.
const _CP1252_TO_BYTE = {
  0x20AC: 0x80, 0x201A: 0x82, 0x0192: 0x83, 0x201E: 0x84, 0x2026: 0x85, 0x2020: 0x86,
  0x2021: 0x87, 0x02C6: 0x88, 0x2030: 0x89, 0x0160: 0x8A, 0x2039: 0x8B, 0x0152: 0x8C,
  0x017D: 0x8E, 0x2018: 0x91, 0x2019: 0x92, 0x201C: 0x93, 0x201D: 0x94, 0x2022: 0x95,
  0x2013: 0x96, 0x2014: 0x97, 0x02DC: 0x98, 0x2122: 0x99, 0x0161: 0x9A, 0x203A: 0x9B,
  0x0153: 0x9C, 0x017E: 0x9E, 0x0178: 0x9F,
};

// Map a Unicode code point back to the single byte that, mis-decoded as
// Latin-1/Windows-1252, would have produced it: 0x00-0xFF map to themselves, the
// Windows-1252 high range (0x80-0x9F) comes from the reverse table above. Returns
// -1 for anything that cannot be a single mis-decoded byte (genuine emoji/CJK).
function _charToByte(code) {
  if (code <= 0xFF) return code;
  const b = _CP1252_TO_BYTE[code];
  return b === undefined ? -1 : b;
}

// Fast pre-check so clean strings skip the work: every UTF-8 byte that got
// mis-decoded as Latin-1/CP1252 begins with one of these lead-byte characters
// (À-ß -> Â/Ã, the smart-punctuation range -> â). Genuine â/Ã/Â text also matches,
// but the fatal decoder below leaves it untouched, so this is only an
// optimisation, never a correctness gate.
const _MOJIBAKE_RE = /[ÂÃâ]/;

const _utf8Fatal = typeof TextDecoder !== 'undefined'
  ? new TextDecoder('utf-8', { fatal: true })
  : null;

/** Repair a single (possibly multi-layer) mojibaked string. No-op on clean text. */
export function repairMojibake(s) {
  if (typeof s !== 'string' || !_utf8Fatal || !_MOJIBAKE_RE.test(s)) return s;
  let cur = s;
  for (let pass = 0; pass < 6; pass++) {
    const bytes = new Uint8Array(cur.length);
    let mappable = true;
    for (let i = 0; i < cur.length; i++) {
      const b = _charToByte(cur.charCodeAt(i));
      if (b === -1) { mappable = false; break; }   // genuine Unicode present -> not mojibake
      bytes[i] = b;
    }
    if (!mappable) break;
    let decoded;
    try { decoded = _utf8Fatal.decode(bytes); }
    catch (e) { break; }                            // bytes are not valid UTF-8 -> no (more) mojibake
    if (decoded === cur || decoded.indexOf('�') !== -1) break;
    cur = decoded;
  }
  return cur;
}

/** Recursively repair every string in a parsed JSON value (object/array/string). */
export function fixEncoding(v) {
  if (typeof v === 'string') return repairMojibake(v);
  if (Array.isArray(v)) return v.map(fixEncoding);
  if (v && typeof v === 'object') {
    const o = {};
    for (const k in v) if (Object.prototype.hasOwnProperty.call(v, k)) o[k] = fixEncoding(v[k]);
    return o;
  }
  return v;
}
